import React, { useMemo, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Menu,
  MenuItem,
  Divider,
  ThemeProvider,
  CssBaseline
} from '@mui/material';
import { buildTheme, radius, NAV_BOTTOM_OFFSET } from './theme';
import { ToastProvider } from './Components/ui';
import {
  HomeRounded as Home,
  RestaurantMenuRounded as RestaurantMenu,
  EmojiEventsRounded as EmojiEvents,
  MoreHorizRounded as MoreHoriz,
  PersonRounded as Person,
  InfoRounded as Info,
  LogoutRounded as Logout,
} from '@mui/icons-material';
import AuthChecker from './Components/AuthChecker';
import Login from './Login/Login';
import Register from './Login/Register';
import Dashboard from './Dashboard/Dashboard';
import BreakfastPage from './Foodtracking/BreakfastPage';
import LunchPage from './Foodtracking/LunchPage';
import DinnerPage from './Foodtracking/DinnerPage';
import SnacksPage from './Foodtracking/SnacksPage';
import QuickTrackPage from './Foodtracking/QuicktrackPage';
import Dishes from './Dishes/Dishes';
import Challenge from './Challenge/Challenge';
import InviteAccept from './Challenge/InviteAccept';
import Profile from './More/Profile';
import Usersettings from './More/Settings';
import About from './More/About';

function App() {
  // 💡 Theme-Handling (Light / Dark)
  const [mode, setMode] = useState('light');

  // Cookie beim Laden auslesen
  useEffect(() => {
    const cookieTheme = document.cookie
      .split('; ')
      .find((row) => row.startsWith('theme='))
      ?.split('=')[1];
    if (cookieTheme) {
      setMode(cookieTheme);
    }
  }, []);

  // Theme-Konfiguration
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <Router>
          <div className="App">
            <AuthChecker>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dishes" element={<Dishes />} />
                <Route path="/challenges" element={<Challenge />} />
                <Route path="/invite/:code" element={<InviteAccept />} />
                <Route path="/settings" element={<Usersettings />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/about" element={<About />} />
                <Route path="/breakfast" element={<BreakfastPage />} />
                <Route path="/lunch" element={<LunchPage />} />
                <Route path="/dinner" element={<DinnerPage />} />
                <Route path="/snacks" element={<SnacksPage />} />
                <Route path="/quickfood" element={<QuickTrackPage />} />
              </Routes>
            </AuthChecker>
            <BottomNav mode={mode} setMode={setMode} />
          </div>
        </Router>
      </ToastProvider>
    </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

// 🌈 Navigationsleiste mit "More"-Menü inkl. Logout
const BottomNav = ({ mode, setMode }) => {
  const [value, setValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const open = Boolean(anchorEl);

  // Auf Login/Register nicht anzeigen
  if (['/login', '/register'].includes(location.pathname)) return null;

  const handleNavigationChange = (event, newValue) => {
    setValue(newValue);
    if (newValue === 0) navigate('/dashboard');
    if (newValue === 1) navigate('/dishes');
    if (newValue === 2) navigate('/challenges');
    if (newValue === 3) setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  // 🌙 Dark/Light Theme Toggle mit Cookie
  const toggleTheme = () => {
    const next = mode === 'light' ? 'dark' : 'light';
    setMode(next);
    document.cookie = `theme=${next};path=/;max-age=31536000`;
  };

  // 🚪 Logout-Funktion
  const handleLogout = () => {
    localStorage.removeItem('token');
    handleClose();
    navigate('/login');
  };

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: NAV_BOTTOM_OFFSET,
          left: 16,
          right: 16,
          borderRadius: radius.pill,
        }}
        elevation={3}
      >
        <BottomNavigation
          sx={{ backgroundColor: 'transparent' }}
          value={value}
          onChange={handleNavigationChange}
        >
          <BottomNavigationAction label="Dashboard" icon={<Home />} />
          <BottomNavigationAction label="Gerichte" icon={<RestaurantMenu />} />
          <BottomNavigationAction label="Challenge" icon={<EmojiEvents />} />
          <BottomNavigationAction label="More" icon={<MoreHoriz />} />
        </BottomNavigation>
      </Paper>

      {/* Dropdown-Menü für "More" */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: 2 }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            navigate('/profile');
          }}
        >
          <Person sx={{ mr: 1 }} /> Profil
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleClose();
            navigate('/about');
          }}
        >
          <Info sx={{ mr: 1 }} /> About
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            toggleTheme();
            // Menü bleibt offen, damit man direkt wieder zurückschalten kann
          }}
        >
          {mode === 'dark' ? '☀️ Zu Light wechseln' : '🌙 Zu Dark wechseln'}
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleLogout}
          sx={{ color: 'error.main', fontWeight: 'bold' }}
        >
          <Logout sx={{ mr: 1 }} /> Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default App;
